import { computed, nextTick, watchEffect } from 'vue'

import { storeToRefs } from 'pinia'

import createLogger from '@/lib/logger'
import { isPosthogFlagOn } from '@/lib/posthog'
import { useAuthStore } from '@/store/auth'
import { useNotificationsStore } from '@/store/notifications'
import { usePwaStore } from '@/store/pwa'

import {
  getInAppNotificationConfigs,
  type InAppNotificationConfig,
} from '../inAppNotifications.config'

interface NotificationRuntime {
  shouldShow: () => boolean
  onAction: () => void | Promise<void>
  onClose: () => void
}

const logger = createLogger('useInAppNotifications')

const sessionDismissStorageKey = (
  notificationConfig: InAppNotificationConfig,
  userId: string,
) => `evergpt-inapp-session-dismissed:${notificationConfig.id}:${userId}`

function isSessionDismissed(
  notificationConfig: InAppNotificationConfig,
  userId: string,
) {
  try {
    return !!sessionStorage.getItem(
      sessionDismissStorageKey(notificationConfig, userId),
    )
  } catch {
    return false
  }
}

const setSessionDismissed = (
  notificationConfig: InAppNotificationConfig,
  userId: string,
) =>
  sessionStorage.setItem(
    sessionDismissStorageKey(notificationConfig, userId),
    '1',
  )

export function useInAppNotifications() {
  const { user } = storeToRefs(useAuthStore())
  const notifications = useNotificationsStore()
  const { current } = storeToRefs(notifications)
  const { showInstallNotification, showUpdateNotification } =
    storeToRefs(usePwaStore())

  const notificationConfigs = getInAppNotificationConfigs()

  const dismissAfter = () => nextTick(() => notifications.dismissCurrent())

  /**
   * Who the session dismissal is keyed to. The PWA prompts run before `/me`, so
   * a visitor with no user yet shares the `guest` scope rather than being held
   * back until one arrives.
   */
  const sessionScope = () => user.value?.id ?? 'guest'

  const isNotificationEligible = (
    notificationConfig: InAppNotificationConfig,
  ): boolean => {
    void user.value
    void showInstallNotification.value
    void showUpdateNotification.value

    if (!isPosthogFlagOn(notificationConfig.posthogFeatureFlag)) return false

    if (isSessionDismissed(notificationConfig, sessionScope())) return false

    return notificationConfig.isEligible?.() ?? true
  }

  const buildRuntime = (
    notificationConfig: InAppNotificationConfig,
  ): NotificationRuntime => ({
    shouldShow: () => isNotificationEligible(notificationConfig),
    onAction: async () => {
      await notificationConfig.actionButton?.onClick()
      if (notificationConfig.skipSessionDismissOnAction) return

      setSessionDismissed(notificationConfig, sessionScope())
      dismissAfter()
    },
    onClose: () => {
      notificationConfig.onDismiss?.()
      if (!notificationConfig.skipSessionDismissOnClose) {
        setSessionDismissed(notificationConfig, sessionScope())
      }
      dismissAfter()
    },
  })

  const runtimesByNotificationId = Object.fromEntries(
    notificationConfigs.map(
      (notificationConfig) =>
        [notificationConfig.id, buildRuntime(notificationConfig)] as const,
    ),
  )

  watchEffect(() => {
    try {
      const currentQueueItem = current.value
      if (currentQueueItem) {
        const runtime = runtimesByNotificationId[currentQueueItem.configId]
        if (runtime && !runtime.shouldShow()) {
          notifications.dismissCurrent()
        }
      }

      for (const notificationConfig of notificationConfigs) {
        if (notifications.isPending(notificationConfig.id)) continue

        if (runtimesByNotificationId[notificationConfig.id]?.shouldShow()) {
          notifications.enqueue(notificationConfig.id)
        }
      }
    } catch (error) {
      logger.error('Error enqueueing in-app notifications', error)
    }
  })

  const active = computed(() => {
    const currentQueueItem = current.value
    if (!currentQueueItem) return null
    const notificationConfig = notificationConfigs.find(
      (candidate) => candidate.id === currentQueueItem.configId,
    )
    if (!notificationConfig) return null
    return {
      queueItemId: currentQueueItem.id,
      title: notificationConfig.title,
      description: notificationConfig.description,
      icon: notificationConfig.headerIcon,
      actionButton: notificationConfig.actionButton,
    }
  })

  const handlePrimaryAction = async () => {
    const currentQueueItem = current.value
    if (!currentQueueItem) return
    await runtimesByNotificationId[currentQueueItem.configId]?.onAction()
  }

  const handleClose = () => {
    const currentQueueItem = current.value
    if (!currentQueueItem) return
    runtimesByNotificationId[currentQueueItem.configId]?.onClose()
  }

  return {
    active,
    handlePrimaryAction,
    handleClose,
  }
}
